import React, { useState } from "react";
import theme from "../../../../styles/theme";

/* ============================================================================
   JourneyTabsCorelia.jsx
   ----------------------------------------------------------------------------
   COMPOSANT DE PARCOURS UTILISATEUR / ONGLETS ÉTAPES

   OBJECTIF
   - Remplacer une barre d’onglets classique par un parcours utilisateur léger
   - Conserver une lecture simple et premium
   - Permettre une réutilisation dans n’importe quelle page Corelia

   ÉLÉMENTS VISUELS
   - titre d’étape
   - symbole image
   - texte contextuel sous le symbole uniquement sur l’étape active

   PROPS PRINCIPALES
   - tabs                : [{ label, content }]
   - activeTab           : index de l’étape active
   - onTabChange         : fonction de changement d’étape
   - activeStepTexts     : tableau des textes à afficher pour chaque étape active
   - componentWidth      : largeur globale du composant (ex: "60%")
   - stepsWidth          : largeur interne pour piloter l’entraxe (ex: "80%")
   - componentOffsetLeft : décalage horizontal local du bloc de navigation
   - headerPaddingLeft   : padding gauche interne du header de navigation
   - tabsDisabled        : désactive les clics
   - containerStyle      : surcharge de style externe

   CONVENTIONS
   - respecte theme.js
   - structure sobre et stable
   - commentaires explicites pour maintenance future
============================================================================ */

const JourneyTabsCorelia = ({
  tabs = [],
  activeTab = 0,
  onTabChange,
  activeStepTexts = [],
  tabsDisabled = false,
  componentWidth = "60%",
  stepsWidth = "80%",
  componentOffsetLeft = 0,
  headerPaddingLeft = 0,
  containerStyle = {},
}) => {
  const [hoveredStep, setHoveredStep] = useState(null);

  /* ==========================================================================
     GARDE-FOUS
  ========================================================================== */
  if (!Array.isArray(tabs) || tabs.length === 0) return null;

  const safeActiveTab = Math.max(0, Math.min(activeTab, tabs.length - 1));

  /* ==========================================================================
     TOKENS VISUELS
  ========================================================================== */
  const donutSize = 20;
  const circleSize = 30;

  const titleFontWeight = theme.fontWeights.normal;
  const activeTextFontSize = theme.fontSizes.sm;

  const headerPaddingTop = theme.spacing.lg;

  /* ==========================================================================
     HELPERS
  ========================================================================== */
  const getActiveStepText = (index) => {
    return activeStepTexts[index] || `Étape ${index + 1}`;
  };

  const handleStepClick = (index) => {
    if (tabsDisabled) return;
    if (typeof onTabChange === "function") onTabChange(index);
  };

  /* ==========================================================================
     RENDER
  ========================================================================== */
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
        fontFamily: theme.fonts.body,
        ...containerStyle,
      }}
    >
      <div
        style={{
          flex: "0 0 auto",
          width: componentWidth,
          minWidth: 0,
          marginLeft: componentOffsetLeft,
          marginBottom:-10,
          marginTop:-5,
          paddingLeft: headerPaddingLeft,
          paddingTop: headerPaddingTop,
          paddingBottom: 0,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: stepsWidth,
            minWidth: 0,
            
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {tabs.map((tab, index) => {
            const isActive = index === safeActiveTab;
            const isReached = index <= safeActiveTab;
            const isHovered = hoveredStep === index;

            return (
              <div
                key={index}
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "default",
                  position: "relative",
                  userSelect: "none",
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    marginBottom: "-5px",
                    fontSize: theme.fontSizes.v0,
                    fontWeight: titleFontWeight,
                    color: theme.colors.text.small,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {tab.label}
                </div>

                <div
                  onClick={() => handleStepClick(index)}
                  onMouseEnter={() => setHoveredStep(index)}
                  onMouseLeave={() => setHoveredStep(null)}
                  style={{
                    width: circleSize,
                    height: circleSize,
                    position: "relative",
                    cursor: tabsDisabled ? "not-allowed" : "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      width: donutSize,
                      height: donutSize,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxSizing: "border-box",
                      position: "relative",
                      zIndex: 1,
                      transform: isActive
                        ? "scale(1.15)"
                        : isHovered
                        ? "scale(0.95)"
                        : "scale(0.85)",
                      opacity: isReached ? 1 : 0.9,
                      transition: "transform 120ms ease, opacity 120ms ease",
                    }}
                  >
                    <img
                      src="/assets/logos/corelia.png"
                      alt="Step"
                      style={{
                        width: donutSize,
                        height: donutSize,
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </div>

                  {/* {isActive && (
                    <div
                      style={{
                        marginTop: theme.spacing.xs,
                        fontSize: activeTextFontSize,
                        color: theme.colors.text.secondary,
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getActiveStepText(index)}
                    </div>
                  )} */}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          width: "100%",
          minWidth: 0,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background:
            containerStyle.background || theme.colors.background.app,
          fontSize: theme.fontSizes.md,
          color: theme.colors.text.body,
          boxSizing: "border-box",
        }}
      >
        {tabs[safeActiveTab]?.content}
      </div>
    </div>
  );
};

export default JourneyTabsCorelia;